<template>
  <auth-layout>
    <main class="login">
      <img
        class="login_logo"
        src="../../assets/img/logo.svg"
        title="SECOM Sights logo"
        alt=""
      />
      <section class="login_cards">
        <template v-if="currentStep === 0">
          <LoginForm @submit="handleSubmit" />
        </template>
        <login-code-form
          v-if="currentStep === 1"
          :mode="verifyMode"
          :loading="loading"
          :error-message="loginCodeError"
          @submit="handleSubmitCode"
          @return="handleReturnToLogin"
          @offMfa="handleOffMFA"
        />
        <off-mfa
          v-if="currentStep === 2"
          :loading="loading"
          @submit="handleSubmitOffMFA"
          @cancel="handleReturnToLogin"
        />
        <approved-off-mfa
          v-if="currentStep === 3"
          @confirm="handleConfirmResetMFA"
          @return="handleReturnToLogin"
        />
      </section>
    </main>
  </auth-layout>
</template>

<script>
import AuthLayout from '@/layouts/AuthLayout.vue';
import LoginForm from './LoginForm.vue';
import LoginCodeForm from './LoginCodeForm.vue';
import { authService } from '@/services/auth.service';
import { initWebPushNotifiacation } from '@/utils/sw';
import OffMfa from './OffMfa.vue';
import ApprovedOffMfa from './ApprovedOffMfa.vue';
import { dialogService } from '@/services/dialog.service';

export default {
  name: 'Login',
  components: {
    LoginForm,
    LoginCodeForm,
    AuthLayout,
    OffMfa,
    ApprovedOffMfa,
  },
  data() {
    return {
      currentStep: 0,
      user: null,
      verifyMode: null,
      loading: false,
      email: null,
      password: null,
      loginCodeError: null,
    };
  },
  computed: {
    routeName() {
      return this.$route.name;
    },
  },
  watch: {
    currentStep() {
      this.loginCodeError = null;
    },
  },
  beforeDestroy() {
    this.loginCodeError = null;
  },
  created() {
    // Reload the login screen when the logout operation is performed
    if (this.$route.query.reload) {
      window.location.assign(this.$route.path);
    }
  },
  mounted() {
    if (this.$route.name === 'login-mfa-reset') {
      this.currentStep = 3;
    }
  },
  methods: {
    handleReturnToLogin() {
      this.currentStep = 0;
      this.routerPushLoginMobile(); // open mobile app if running in mobile app
    },
    async handleSubmit({ mfa, email, password }) {
      if (
        (mfa && mfa.status === 'SOFTWARE_TOKEN_MFA') ||
        mfa.status === 'SMS_MFA'
      ) {
        this.user = mfa.user;
        this.verifyMode = mfa.status;
        this.email = email;
        this.password = password;
        this.currentStep = 1;
        return;
      }
    },
    async handleSubmitCode({ code }) {
      this.loginCodeError = null;
      try {
        if (code) {
          this.loading = true;
          await authService.confirmSignInCode(this.user, code, this.verifyMode);

          await initWebPushNotifiacation().then(() => {
            this.routerPush({ name: 'home' });
          });
        } else {
          this.currentStep = 0;
        }
      } catch (error) {
        this.loginCodeError = '認証コードが正しくありません';
        console.log('error on handling verify code', error);
      } finally {
        this.loading = false;
      }
    },
    handleOffMFA() {
      this.currentStep = 2;
    },
    async handleSubmitOffMFA() {
      this.loginCodeError = null;
      try {
        this.loading = true;
        await authService.resetUserMFA(this.user.username);
        dialogService.showConfirmDialog(
          {
            title: '多要素認証解除要求を受け付けました。',
            content: `解除実行URLをメールアドレスへ送信しました。`,
            cancel: false,
          },
          {
            classes: ['forgot_dialog'],
            width: 575,
            height: 238,
            adaptive: true,
          },
          () => {
            this.handleReturnToLogin();
          }
        );
      } catch (error) {
        console.log('error on handling off MFA', error);
      } finally {
        this.loading = false;
      }
    },
    async handleConfirmResetMFA(token) {
      this.loginCodeError = null;
      try {
        this.loading = true;
        await authService.resetUserMFAByToken(token);
      } catch (error) {
        console.log('error on handling confirm reset MFA', error);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss">
.login {
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  height: 100%;

  // mobile-friendly dialog styling for login/MFA reset context
  &_dialog {
    &.vm--modal {
      border-radius: 6px !important;
      box-shadow: none;
      padding: 0px 15px;
      background: transparent;
    }
    .confirmedDialog {
      &_actions {
        &.v-card__actions {
          margin-bottom: 0;
        }
      }
      .dialog_closeIcon.v-btn.v-btn--icon.v-size--default {
        top: 0;
      }
      position: relative;
      &_actions {
        .confirmedDialog_btn-ok {
          &.v-btn:not(.v-btn--round).v-size--default {
            height: 30px;
          }
        }
      }
    }
  }

  &_cards {
    width: 100%;
  }
  &_logo {
    display: block;
    height: 24px;
    width: 165.4px;
    margin-bottom: 30px;
    margin-top: 40px;
  }
  @media (max-width: 576px) {
    &_dialog {
      .confirmedDialog_card {
        &.v-sheet {
          padding: 15px;
        }
      }
    }
    .v-sheet.v-card:not(.v-sheet--outlined) {
      width: 100%;
      padding: 20px;
    }
    &_cards {
      padding: 20px;
    }
    &_header-gap {
      display: none;
    }
    &_logo {
      margin-bottom: 0px;
      margin-top: 21px;
    }
  }
  &_footer {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    margin-top: 40px;

    &-title {
      font-size: 14px;
      font-weight: normal;
      font-stretch: normal;
      font-style: normal;
      letter-spacing: normal;
      text-align: left;
      color: #333;
    }
    &-caption {
      margin: 10px 0;
      font-size: 12px;
      font-weight: normal;
      font-stretch: normal;
      font-style: normal;
      letter-spacing: normal;
      text-align: center;
      color: #989898;
    }
  }
}
</style>
